<template>
  <woot-modal
    :show="show"
    :on-close="onClose"
    :show-close-button="false"
    size="!w-[min(90vw,1100px)] !max-w-none h-[85vh] rounded-xl overflow-hidden"
  >
    <div
      v-if="activeAttachment"
      class="attachment-preview flex flex-col h-full"
    >
      <div
        class="flex items-center gap-3 h-14 px-4 shrink-0 border-b border-solid border-gray-100 dark:border-slate-700"
      >
        <div class="flex flex-col min-w-0 flex-1">
          <span
            class="text-sm leading-5 text-gray-900 dark:text-slate-100 truncate"
          >
            {{ fileName }}
          </span>
          <span
            v-if="fileSize"
            dir="ltr"
            class="text-xs leading-[18px] text-gray-500 dark:text-slate-400"
          >
            {{ fileSize }}
          </span>
        </div>
        <span
          v-if="hasMoreThanOne"
          dir="ltr"
          class="text-xs text-gray-500 dark:text-slate-400 shrink-0"
        >
          {{ `${activeIndex + 1} / ${attachments.length}` }}
        </span>
        <span
          v-if="objectUrl"
          v-tooltip.top="$t('CONVERSATION.DOWNLOAD')"
          class="material-symbols-outlined preview-action"
          @click="onDownload"
        >
          download
        </span>
        <span class="material-symbols-outlined preview-action" @click="onClose">
          close
        </span>
      </div>
      <div
        class="flex flex-1 min-h-0 items-center gap-2 p-4 bg-gray-50 dark:bg-slate-900"
      >
        <div class="w-10 shrink-0 flex justify-center">
          <span
            v-if="hasMoreThanOne"
            class="material-symbols-outlined preview-action rtl:rotate-180"
            :class="{ disabled: !hasPrevious }"
            @click="showPrevious"
          >
            chevron_left
          </span>
        </div>
        <div class="flex flex-1 min-w-0 h-full items-center justify-center">
          <template v-if="objectUrl">
            <img
              v-if="isImage"
              :src="objectUrl"
              :alt="fileName"
              class="max-w-full max-h-full object-contain rounded-lg"
            />
            <video
              v-else-if="isVideo"
              :src="objectUrl"
              controls
              playsinline
              class="max-w-full max-h-full rounded-lg"
            />
            <audio v-else-if="isAudio" :src="objectUrl" controls />
            <iframe
              v-else-if="isPdf"
              :src="objectUrl"
              :title="fileName"
              class="w-full h-full border-0 rounded-lg bg-white"
            />
            <div
              v-else
              class="flex flex-col items-center gap-3 text-center text-gray-600 dark:text-slate-300"
            >
              <span class="material-symbols-outlined text-[64px] text-gray-400">
                draft
              </span>
              <span class="text-sm">
                {{ $t('CONVERSATION.REPLYBOX.NO_PREVIEW_AVAILABLE') }}
              </span>
              <woot-button size="small" @click="onDownload">
                {{ $t('CONVERSATION.DOWNLOAD') }}
              </woot-button>
            </div>
          </template>
          <spinner v-else />
        </div>
        <div class="w-10 shrink-0 flex justify-center">
          <span
            v-if="hasMoreThanOne"
            class="material-symbols-outlined preview-action rtl:rotate-180"
            :class="{ disabled: !hasNext }"
            @click="showNext"
          >
            chevron_right
          </span>
        </div>
      </div>
    </div>
  </woot-modal>
</template>

<script>
import Spinner from 'shared/components/Spinner.vue';
import { formatBytes } from 'shared/helpers/FileHelper';
import rtlMixin from 'shared/mixins/rtlMixin';

export default {
  components: { Spinner },
  mixins: [rtlMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    attachments: {
      type: Array,
      default: () => [],
    },
    initialIndex: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      activeIndex: this.initialIndex,
      objectUrl: '',
    };
  },
  computed: {
    activeAttachment() {
      return this.attachments[this.activeIndex];
    },
    resource() {
      return this.activeAttachment?.resource || {};
    },
    contentType() {
      return this.resource.content_type || this.resource.type || '';
    },
    fileName() {
      return this.resource.filename || this.resource.name || '';
    },
    fileSize() {
      const size = this.resource.byte_size || this.resource.size;
      return size ? formatBytes(size, 0) : '';
    },
    isImage() {
      return this.contentType.startsWith('image/');
    },
    isVideo() {
      return this.contentType.startsWith('video/');
    },
    isAudio() {
      return this.contentType.startsWith('audio/');
    },
    isPdf() {
      return this.contentType === 'application/pdf';
    },
    hasMoreThanOne() {
      return this.attachments.length > 1;
    },
    hasPrevious() {
      return this.activeIndex > 0;
    },
    hasNext() {
      return this.activeIndex < this.attachments.length - 1;
    },
  },
  watch: {
    initialIndex(index) {
      this.activeIndex = index;
    },
    activeAttachment: {
      immediate: true,
      handler() {
        this.loadObjectUrl();
      },
    },
    attachments(list) {
      if (!list.length) {
        this.onClose();
      } else if (this.activeIndex > list.length - 1) {
        this.activeIndex = list.length - 1;
      }
    },
  },
  mounted() {
    document.addEventListener('keydown', this.onKeyDown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.onKeyDown);
    this.revokeObjectUrl();
  },
  methods: {
    async loadObjectUrl() {
      this.revokeObjectUrl();
      const attachment = this.activeAttachment;
      if (!attachment) return;
      const file = attachment.resource?.file;
      if (file instanceof Blob) {
        this.objectUrl = URL.createObjectURL(file);
        return;
      }
      if (!attachment.thumb) return;
      try {
        const blob = await (await fetch(attachment.thumb)).blob();
        if (attachment !== this.activeAttachment) return;
        this.objectUrl = URL.createObjectURL(blob);
      } catch (error) {
        this.objectUrl = attachment.thumb;
      }
    },
    revokeObjectUrl() {
      if (this.objectUrl.startsWith('blob:')) {
        URL.revokeObjectURL(this.objectUrl);
      }
      this.objectUrl = '';
    },
    showPrevious() {
      if (this.hasPrevious) this.activeIndex -= 1;
    },
    showNext() {
      if (this.hasNext) this.activeIndex += 1;
    },
    onKeyDown(event) {
      if (!this.show) return;
      const backKey = this.isRTLView ? 'ArrowRight' : 'ArrowLeft';
      const forwardKey = this.isRTLView ? 'ArrowLeft' : 'ArrowRight';
      if (event.key === backKey) this.showPrevious();
      if (event.key === forwardKey) this.showNext();
    },
    onDownload() {
      const link = document.createElement('a');
      link.href = this.objectUrl;
      link.download = this.fileName;
      link.click();
    },
    onClose() {
      this.$emit('close');
    },
  },
};
</script>

<style lang="scss" scoped>
.preview-action {
  @apply w-8 h-8 flex items-center justify-center rounded-md cursor-pointer select-none
    text-[24px] text-gray-500 hover:bg-gray-100
    dark:text-slate-400 dark:hover:bg-slate-700;

  &.disabled {
    @apply text-gray-200 cursor-not-allowed hover:bg-transparent dark:text-slate-600;
  }
}
</style>
